/**
 * IOSInstallBanner
 * Banner inferior no-invasivo que aparece automáticamente en iOS Safari
 * para invitar al usuario a instalar la PWA (Safari iOS NO dispara
 * `beforeinstallprompt`, así que el botón PWA estándar queda oculto).
 *
 * - Solo se muestra en iOS Safari real (no Chrome/FF/Edge iOS).
 * - Se oculta si ya está instalado (standalone).
 * - Se oculta si el usuario lo descartó (localStorage, 30 días).
 * - Aparece tras 15s de uso para no ser intrusivo.
 * - Respeta safe-area-inset-bottom (no queda tapado por el home indicator).
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import InstallAppModal from './InstallAppModal';
import { trackPwa } from '../utils/trackPwa';

const DISMISS_KEY = 'damyxia-ios-install-dismissed-at';
const DISMISS_DAYS = 30;
const SHOW_AFTER_MS = 15000;

const isIOSSafari = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (!isIOS) return false;
  // Exclude Chrome iOS (CriOS), Firefox iOS (FxiOS), Edge iOS (EdgiOS), Opera (OPiOS), Brave
  if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA|YaBrowser/.test(ua)) return false;
  // iPadOS 13+ reports as Mac. Detect touch-enabled Mac as iPad.
  // The /iPad/ regex above already catches pre-13 iPads; touch Mac is a grey area,
  // skip it to avoid false positives on real macOS Safari.
  return true;
};

const isStandaloneMode = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const wasRecentlyDismissed = () => {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const diffMs = Date.now() - Number(ts);
    return diffMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const IOSInstallBanner = () => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const txt = {
    es: {
      message: 'Instalá DamyxIA en tu iPhone',
      sub: 'Accedé más rápido, a pantalla completa',
      cta: 'Ver cómo',
      close: 'Cerrar',
    },
    en: {
      message: 'Install DamyxIA on your iPhone',
      sub: 'Faster access, full screen',
      cta: 'Show how',
      close: 'Close',
    },
  };
  const t = txt[language] || txt.es;

  useEffect(() => {
    if (!isIOSSafari()) return;
    const standalone = isStandaloneMode();
    if (standalone) return;
    const dismissedBefore = wasRecentlyDismissed();
    if (dismissedBefore) return;

    const timer = setTimeout(() => {
      setVisible(true);
      trackPwa('ios_install_banner_shown', {
        dismissed_before: dismissedBefore,
        standalone,
      });
    }, SHOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    trackPwa('ios_install_banner_dismissed');
    setVisible(false);
  };

  const handleOpen = () => {
    try {
      localStorage.setItem('damyxia-pwa-cta-at', String(Date.now()));
    } catch {}
    trackPwa('ios_install_banner_cta_clicked');
    setModalOpen(true);
  };

  if (!visible) {
    return modalOpen ? <InstallAppModal isOpen={modalOpen} onClose={() => setModalOpen(false)} /> : null;
  }

  return (
    <>
      {createPortal(
        <div
          className="fixed left-3 right-3 z-[9998] flex items-center gap-3 rounded-2xl border border-teal-400/30 bg-gradient-to-r from-teal-600/95 via-cyan-600/95 to-blue-600/95 px-4 py-3 shadow-2xl shadow-black/40 backdrop-blur-md animate-in fade-in slide-in-from-bottom-4"
          style={{
            bottom: 'calc(12px + env(safe-area-inset-bottom))',
          }}
          data-testid="ios-install-banner"
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{t.message}</p>
            <p className="text-xs text-white/80 truncate">{t.sub}</p>
          </div>
          <button
            onClick={handleOpen}
            className="flex-shrink-0 px-3 py-1.5 bg-white text-teal-700 text-xs font-bold rounded-full hover:bg-white/90 active:scale-95 transition-all"
            data-testid="ios-install-banner-cta"
          >
            {t.cta}
          </button>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label={t.close}
            data-testid="ios-install-banner-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>,
        document.body
      )}
      <InstallAppModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default IOSInstallBanner;
